import { useEffect, useMemo } from "react";

import { ListSubheader, MenuItem, TextField, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useFormikContext } from "formik";

import { cloudflowTexts } from "../../../../assets/texts";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useGetGcpConnectedOrganizationsOrProjects } from "../../hooks";
import { withRootPath } from "./CloudSpecificParameterForm";
import { useFieldCommonProps } from "./useFieldCommonProps";

type GCPParameterFormProps = Readonly<{
  isOperationInputPresent: boolean;
  rootPath?: string;
}>;

export default function GCPParameterForm({ isOperationInputPresent, rootPath }: GCPParameterFormProps) {
  const { customer } = useCustomerContext();
  const formikProps = useFormikContext();
  const { getFieldProps, setFieldValue } = formikProps;

  const serviceAccountResourceField = useFieldCommonProps(
    getFieldProps<string>(withRootPath("serviceAccountResource", rootPath)),
    "Organization or Project",
    true
  );

  const { data: organizationsOrProjects, isLoading: orgLoading } = useGetGcpConnectedOrganizationsOrProjects(
    customer.id
  );

  const groupedOptions = useMemo(() => {
    if (!organizationsOrProjects || orgLoading) return { organizations: [], projects: [] };
    return {
      organizations: organizationsOrProjects.filter((item) => item.scope === "organization"),
      projects: organizationsOrProjects.filter((item) => item.scope === "project"),
    };
  }, [orgLoading, organizationsOrProjects]);

  useEffect(() => {
    if (!orgLoading && !serviceAccountResourceField.value && groupedOptions.organizations.length > 0) {
      const firstOrganization = groupedOptions.organizations[0];
      setFieldValue(withRootPath("serviceAccountResource", rootPath), `organizations/${firstOrganization.id}`);
    }
  }, [orgLoading, serviceAccountResourceField.value, groupedOptions.organizations, setFieldValue, rootPath]);

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack sx={{ gap: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          {cloudflowTexts.GCP_CONFIGURATION_FORM.SELECT_PROJECT_OR_ORGANIZATION}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {cloudflowTexts.GCP_CONFIGURATION_FORM.SELECT_PROJECT_OR_ORGANIZATION_DESCRIPTION}
        </Typography>
      </Stack>

      <TextField
        {...serviceAccountResourceField}
        select
        fullWidth
        variant="outlined"
        onChange={({ target: { value } }) => {
          setFieldValue(withRootPath("serviceAccountResource", rootPath), value);
        }}
        data-cy="gcp-parameter-form-service-account-resource"
      >
        {orgLoading && (
          <MenuItem disabled value="">
            Loading data...
          </MenuItem>
        )}
        {groupedOptions.organizations.length > 0 && <ListSubheader>Organizations</ListSubheader>}
        {groupedOptions.organizations.map((org) => (
          <MenuItem key={org.id} value={`organizations/${org.id}`} data-cy={`gcp-org-option-${org.id}`}>
            {org.displayName}
          </MenuItem>
        ))}
        {groupedOptions.projects.length > 0 && <ListSubheader key="list-header-project">Projects</ListSubheader>}
        {groupedOptions.projects.map((proj) => (
          <MenuItem key={proj.id} value={`projects/${proj.id}`} data-cy={`gcp-project-option-${proj.id}`}>
            {proj.displayName}
          </MenuItem>
        ))}
      </TextField>
      {isOperationInputPresent && (
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          Parameters
        </Typography>
      )}
    </Stack>
  );
}
